import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { personalInfo } from '../../data/portfolio';
import BackgroundSection from '../common/BackgroundSection';
import Navigation from './Navigation';
import Terminal from './Terminal';

const HeroContainer = styled(BackgroundSection)`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  position: relative;
`;

const HeroContent = styled.div`
  position: relative;
  z-index: ${({ theme }) => theme.zIndex.content};
  padding: 60px 20px;
  max-width: 1000px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;

  @media (min-width: 768px) {
    flex-direction: row;
    text-align: left;
    gap: 48px;
  }
`;

const ProfileImageContainer = styled.div`
  position: relative;
  flex-shrink: 0;
  
  @media (max-width: 767px) {
    order: -1;
    margin-bottom: 24px;
  }
`;

const glowPulse = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(125, 211, 252, 0.15), 0 8px 32px rgba(0, 0, 0, 0.3); }
  50% { box-shadow: 0 0 40px rgba(125, 211, 252, 0.25), 0 8px 32px rgba(0, 0, 0, 0.3); }
`;

const ProfileImage = styled.img`
  width: 280px;
  height: 280px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid rgba(125, 211, 252, 0.3);
  animation: ${glowPulse} 4s ease-in-out infinite;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
    border-color: rgba(125, 211, 252, 0.6);
  }

  @media (max-width: 767px) {
    width: 220px;
    height: 220px;
  }

  @media (max-width: 480px) {
    width: 180px;
    height: 180px;
  }
`;

const TextContent = styled.div`
  flex: 1;
  min-width: 0;
  
  @media (max-width: 767px) {
    text-align: center;
  }
`;

const Name = styled(motion.h1)`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Inter', sans-serif;
  font-size: clamp(32px, 5vw, 48px);
  margin: 0 0 8px 0;
  font-weight: 700;
  line-height: 1.2;
  background: linear-gradient(135deg, #ffffff 0%, #7dd3fc 50%, #60a5fa 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (min-width: 768px) {
    font-size: clamp(36px, 4vw, 52px);
  }
`;

const RoleContainer = styled(motion.div)`
  height: 32px;
  overflow: hidden;
  margin-bottom: 16px;
  
  @media (max-width: 767px) {
    display: flex;
    justify-content: center;
  }
`;

const RoleText = styled(motion.span)`
  display: inline-block;
  font-size: clamp(16px, 2.5vw, 20px);
  font-weight: 500;
  color: ${({ theme }) => theme.colors.accent};
  letter-spacing: 0.5px;
`;

const Tagline = styled(motion.p)`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Inter', sans-serif;
  font-size: clamp(14px, 2vw, 16px);
  color: rgba(255, 255, 255, 0.65);
  margin: 0 0 28px 0;
  font-weight: 400;
  line-height: 1.6;
  
  @media (min-width: 768px) {
    max-width: 420px;
  }
  
  @media (max-width: 767px) {
    max-width: 340px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const CTAContainer = styled(motion.div)`
  display: flex;
  gap: 16px;
  margin-top: 8px;

  @media (max-width: 767px) {
    justify-content: center;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
  }
`;

const CTAButton = styled(motion.a)<{ $variant?: 'primary' | 'secondary' }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 28px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;

  ${({ $variant }) => $variant === 'primary' ? `
    background: linear-gradient(135deg, #7dd3fc 0%, #60a5fa 100%);
    color: #0f1724;
    border: none;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(125, 211, 252, 0.3);
      color: #0f1724;
    }
  ` : `
    background: transparent;
    color: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(125, 211, 252, 0.4);

    &:hover {
      transform: translateY(-2px);
      border-color: rgba(125, 211, 252, 0.8);
      background: rgba(125, 211, 252, 0.1);
      color: white;
    }
  `}
`;

const ScrollIndicator = styled(motion.div)`
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const ScrollLine = styled(motion.div)`
  width: 1px;
  height: 30px;
  background: linear-gradient(to bottom, rgba(125, 211, 252, 0.6), transparent);
`;

const ScrollText = styled.span`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
  letter-spacing: 2px;
  text-transform: uppercase;
`;

const TerminalSection = styled.div`
  position: relative;
  z-index: ${({ theme }) => theme.zIndex.content};
  margin-top: 40px;
  padding: 0 20px;
`;

const Hero: React.FC = () => {
  const { t } = useTranslation();
  const [roleIndex, setRoleIndex] = useState(0);
  const roles: string[] = t('hero.roles', { returnObjects: true }) as string[];
  const roleCount = roles.length;

  useEffect(() => {
    const interval = setInterval(() => {
      setRoleIndex((prev) => (prev + 1) % roleCount);
    }, 3000);
    return () => clearInterval(interval);
  }, [roleCount]);

  return (
    <HeroContainer 
      opacity={0.3}
      overlayStyle="rgba(0, 15, 35, 0.8)"
    >
      <div className="container">
        <HeroContent>
          <TextContent>
            <Name
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {personalInfo.name}
            </Name>
            <RoleContainer
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <AnimatePresence mode="wait">
                <RoleText
                  key={roleIndex}
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -20, opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  {roles[roleIndex]}
                </RoleText>
              </AnimatePresence>
            </RoleContainer>
            <Tagline
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              {t('hero.tagline')}
            </Tagline>
            <CTAContainer
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              <CTAButton
                href="#contact"
                $variant="primary"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                {t('hero.getInTouch')}
              </CTAButton>
              <CTAButton
                href="#experience"
                $variant="secondary"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#experience')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                {t('hero.viewWork')}
              </CTAButton>
            </CTAContainer>
          </TextContent>
          
          <ProfileImageContainer>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <ProfileImage 
                src={personalInfo.profileImage} 
                alt={personalInfo.name}
                loading="eager"
              />
            </motion.div>
          </ProfileImageContainer>
        </HeroContent>
        <TerminalSection>
          <Terminal />
        </TerminalSection>
        <Navigation />
        <ScrollIndicator
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.8 }}
        >
          <ScrollText>{t('hero.scroll')}</ScrollText>
          <ScrollLine
            animate={{ scaleY: [0, 1, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            style={{ originY: 0 }}
          />
        </ScrollIndicator>
      </div>
    </HeroContainer>
  );
};

export default Hero;